import React, { useState, useEffect, useContext, useCallback } from 'react'
import { Badge, Button, Menu, MenuProps, Tag, Tooltip, Dropdown, Typography } from 'antd'
import { useLocation, useHistory, Link } from 'react-router-dom'
import { isBefore, differenceInDays } from 'date-fns'
import { StarOutlined, GiftOutlined, DownOutlined, UpOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'
import { PiLightning } from 'react-icons/pi'

import { MenuContext } from '../../context/menuContext'
import { SURVEY_TRIAL_EXTEND } from '../../components/trial-popup-survey'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectAuthCompanySlice } from '../../store/auth-company-slice'
import { useEffectivePlan } from '../../store/use-effective-plan'
import { selectFeatureFlagsSlice } from '../../store/feature-flags-slice'
import { selectAuthUserSlice } from '../../store/auth-user-slice'
import { handleOnboardingVisible, selectOnboardingSlice } from '../../store/onboarding-slice'
import { sidebarTracker } from './services/sidebarTracking'
import { useProfileMenuItems } from '../../hooks/use-profile-menu-items'

import CustomScrollbars from '../../util/CustomScrollbars'
import IntlMessages from '../../util/IntlMessages'
import SidebarLogo from './SidebarLogo'
import menus, { requestLeaveOptions, canShowRequestToil } from './main-menu'

import { Platform } from '@vacationtracker/shared/types/core-event'
import { FrontendUrls } from '../../types/urls'
import { getWizardState, isWizardDismissed, WIZARD_STATE_CHANGED_EVENT } from '../../util/onboarding-wizard-storage'
import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'
import { useFeatureFlagEnabled } from 'posthog-js/react'
import { useIsPostHogFeatureFlagEnabledForCompany } from '@vacationtracker/shared/hooks/use-is-postHog-featureFlag-enabled-for-company'
import { UserAvatar } from '@vacationtracker/shared/components/user-avatar'
import { ICompanyInfo, SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { resolveFeaturePermissions } from '@vacationtracker/shared/functions/resolve-feature-permissions'
import { IFeatureVisibility } from '@vacationtracker/shared/types/company'

interface ISidebarContent {
  onLogout: Function
  isMobileDrawer?: boolean
  onItemClick?: () => void
}

interface IMenuLike {
  path?: string
  children?: IMenuLike[]
}

/**
 * The menu key to highlight for a route, including detail pages under it.
 *
 * Has to walk children as well as top level: items inside a collapsible section (Locations, Leave
 * Types, Billing, Reports...) hang off a header whose key is synthetic ('__section__leaveSetup'),
 * so matching only the top level left every one of their sub-routes with nothing selected, while
 * top-level items like Departments highlighted fine.
 *
 * Longest match wins, and only on a path-segment boundary so '/app/users' never claims
 * '/app/users-import'.
 */
export const selectableMenuKeyFor = (pathname: string, menus: IMenuLike[]): string | undefined =>
  menus
    .flatMap((item) => [item, ...(item.children ?? [])])
    .map((item) => item.path)
    .filter((path): path is string => Boolean(path) && !path?.startsWith('__section__'))
    .filter((path) => pathname === path || pathname.startsWith(`${path}/`))
    .sort((a, b) => b.length - a.length)[0]

const SidebarContent = ({ onLogout, isMobileDrawer = false, onItemClick }: ISidebarContent) => {
  const { authUser, isLoadingAuthUser } = useAppSelector(selectAuthUserSlice)
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const effectivePlan = useEffectivePlan()
  const { featureFlags } = useAppSelector(selectFeatureFlagsSlice)
  const { progressCompleted, totalChecklist, hideOnboardingFlow, onboardingCompleted, onboardingSkipped } = useAppSelector(selectOnboardingSlice)
  const dispatch = useAppDispatch()
  const aiAssistedOnboardingEnabled = useFeatureFlagEnabled(FeatureFlagEnum.aiAssistedOnboarding)
  const onboardingV2Enabled = useFeatureFlagEnabled(FeatureFlagEnum.onboardingV2)
  const { enabled: teamEnergyEnabled } = useIsPostHogFeatureFlagEnabledForCompany(FeatureFlagEnum.teamEnergy, authCompany?.id, authUser.id)
  const resolvedPermissions = React.useMemo(
    () => resolveFeaturePermissions((authCompany as any) || {}), // eslint-disable-line @typescript-eslint/no-explicit-any
    [authCompany?.featurePermissions]
  )

  // A location admin who is also an approver behaves like an approver, but only for menu items
  // explicitly flagged with allowLocationAdminApprover. Their role is masked to LocationAdmin,
  // so the approver capability is recovered from approverTo.
  const isApproverLocationAdmin = authUser.role === 'LocationAdmin' && authUser.approverTo.length > 0

  const { navCollapsed, miniMenu, setNavCollapsed, changeSelectedKeys } = useContext(MenuContext)

  const [hasWizardState, setHasWizardState] = useState(
    () =>
      authCompany?.platform === 'email' && authUser.role === 'Admin' && !isWizardDismissed(authUser.id, authCompany?.id) && getWizardState(authUser.id, authCompany?.id) !== null
  )

  useEffect(() => {
    const handler = (e: Event) => {
      const hasState = (e as CustomEvent<{ hasState: boolean }>).detail.hasState
      setHasWizardState(hasState && authCompany?.platform === 'email' && authUser.role === 'Admin')
    }
    window.addEventListener(WIZARD_STATE_CHANGED_EVENT, handler)
    return () => window.removeEventListener(WIZARD_STATE_CHANGED_EVENT, handler)
  }, [authCompany?.platform, authUser.role])

  const history = useHistory()
  const { pathname } = useLocation()

  const [rootSubmenuKeys] = useState(menus.map((item) => item.path))
  const [openKeys, setOpenKeys] = useState(() => {
    let subMenu
    rootSubmenuKeys.forEach((key) => {
      if (pathname.startsWith(key)) {
        subMenu = key
      }
    })
    return subMenu ? [subMenu] : [pathname]
  })
  const [selectedKeys, setSelectedKeys] = useState<string[]>()

  // Initialize tracking on mount
  useEffect(() => {
    sidebarTracker.init()
  }, [])

  // Helper function to check if current path belongs to a section
  const getPathSection = useCallback((currentPath: string) => {
    // LEAVE SETUP section paths
    if (currentPath.startsWith('/app/settings/locations') || currentPath.startsWith('/app/settings/leave-types') || currentPath.startsWith('/app/settings/leave-policies')) {
      return '__section__leaveSetup'
    }

    // WORKSPACE SETTINGS section paths
    if (
      currentPath.startsWith('/app/settings/general') ||
      currentPath.startsWith('/app/settings/features-permissions') ||
      currentPath.startsWith('/app/add-ons') ||
      currentPath.startsWith('/app/integrations') ||
      currentPath.startsWith('/app/imports') ||
      currentPath.startsWith('/app/settings/billing')
    ) {
      return '__section__workspaceSettings'
    }

    // REPORTS section paths
    if (currentPath.startsWith('/app/reports')) {
      return '__section__reports'
    }

    return null
  }, [])

  const shouldCollapseSections = (authCompany: ICompanyInfo | null) => {
    if (!authCompany) {
      return false
    }
    return (
      (authCompany?.plan !== SubscriptionPlanEnum.freemium && authCompany?.trialPeriod !== 'in_trial') ||
      (authCompany?.plan === SubscriptionPlanEnum.freemium && authCompany?.timestamp && differenceInDays(new Date(), new Date(authCompany.timestamp)) > 7)
    )
  }
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => {
    // Initially collapse collapsible sections when company is not in trial
    // but keep the current section expanded if user is viewing a page within it
    const initialCollapsed = new Set<string>()
    if (shouldCollapseSections(authCompany)) {
      const currentSection = getPathSection(pathname)

      // Only collapse sections that are not currently active
      if (currentSection !== '__section__leaveSetup') {
        initialCollapsed.add('__section__leaveSetup')
      }
      if (currentSection !== '__section__workspaceSettings') {
        initialCollapsed.add('__section__workspaceSettings')
      }
      if (currentSection !== '__section__reports') {
        initialCollapsed.add('__section__reports')
      }
    }
    return initialCollapsed
  })

  useEffect(() => {
    const findRootKey = (path) => {
      const rootPath = menus.find((item) => path.startsWith(item.path))
      return rootPath ? rootPath.path : '/'
    }

    const rootKey = findRootKey(pathname)
    setSelectedKeys([pathname, selectableMenuKeyFor(pathname, menus), rootKey].filter(Boolean) as string[])
    setOpenKeys((prevKeys) => [...new Set([...prevKeys, rootKey])])
  }, [pathname, menus])

  useEffect(() => {
    if (changeSelectedKeys) {
      if (authUser.role === 'Approver') {
        setOpenKeys(['/app/leaves'])
      } else {
        setOpenKeys(['/app/settings'])
      }
    }
  }, [changeSelectedKeys])

  // Update collapsed sections when trial status changes (only when trial status actually changes)
  useEffect(() => {
    if (shouldCollapseSections(authCompany)) {
      // When not in trial, collapse sections but preserve current section
      const currentSection = getPathSection(pathname)
      setCollapsedSections((prev) => {
        const newSet = new Set(prev)
        // Add all collapsible sections except the current one
        if (currentSection !== '__section__leaveSetup') {
          newSet.add('__section__leaveSetup')
        }
        if (currentSection !== '__section__workspaceSettings') {
          newSet.add('__section__workspaceSettings')
        }
        if (currentSection !== '__section__reports') {
          newSet.add('__section__reports')
        }
        return newSet
      })
    } else {
      // If company enters trial, expand all sections
      setCollapsedSections((prev) => {
        const newSet = new Set(prev)
        newSet.delete('__section__leaveSetup')
        newSet.delete('__section__workspaceSettings')
        newSet.delete('__section__reports')
        return newSet
      })
    }
  }, [authCompany?.trialPeriod]) // Only depend on trial status, not pathname

  // Separate effect to handle pathname changes - only expand current section if needed
  useEffect(() => {
    if (shouldCollapseSections(authCompany)) {
      const currentSection = getPathSection(pathname)
      if (currentSection) {
        setCollapsedSections((prev) => {
          if (prev.has(currentSection)) {
            const newSet = new Set(prev)
            newSet.delete(currentSection)
            return newSet
          }
          return prev
        })
      }
    }
  }, [pathname, authCompany?.trialPeriod, getPathSection])

  // Use shared profile menu hook
  const { profileMenuItems, energyScore, shouldShowEnergyScore } = useProfileMenuItems({
    onLogout: () => onLogout(),
    onTrackProfileMenuMyProfileClick: (data) => sidebarTracker.trackProfileMenuMyProfileClick(data),
    onTrackProfileMenuAccountSettingsClick: () => sidebarTracker.trackProfileMenuAccountSettingsClick(),
    onTrackProfileMenuLogoutClick: () => sidebarTracker.trackProfileMenuLogoutClick(),
  })

  const handleItemClickForMobile = () => {
    if (isMobileDrawer && onItemClick) {
      onItemClick()
    }
  }

  const handleMenuClick = (event) => {
    // Find the menu item that was clicked to get its name
    const findMenuItem = (items: any[], key: string): any => {
      for (const item of items) {
        if (item.path === key) return item
        if (item.children) {
          const found = findMenuItem(item.children, key)
          if (found) return found
        }
      }
      return null
    }

    const menuItem = findMenuItem(menus, event.key)

    // Handle onClick handlers (e.g., game modal)
    if (menuItem?.onClick) {
      // TODO IVAN: Open game modal here
      handleItemClickForMobile()
      return
    }

    if (menuItem) {
      sidebarTracker.trackOptionClick({
        optionName: menuItem.title || menuItem.name || event.key,
        optionRoute: event.key,
        sectionName: menuItem.section,
        isMiniMenu: miniMenu,
        isMobileDrawer,
      })
    }

    handleItemClickForMobile()

    if (navCollapsed) {
      setNavCollapsed(!navCollapsed)
    }
    if (event.key.includes('https')) {
      return
    }
    history.push(event.key)
  }

  const handleOpenChange = (event) => {
    setOpenKeys(event)
  }

  const toggleSectionCollapse = (sectionPath: string) => {
    const isCurrentlyCollapsed = collapsedSections.has(sectionPath)
    const action = isCurrentlyCollapsed ? 'open' : 'close'

    // Track section toggle
    sidebarTracker.trackSectionToggle({
      sectionName: sectionPath,
      action,
      isMobileDrawer,
    })

    setCollapsedSections((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(sectionPath)) {
        newSet.delete(sectionPath)
      } else {
        newSet.add(sectionPath)
      }
      return newSet
    })
  }

  const getScrollableContentPadding = (): string => {
    if (isMobileDrawer) {
      return '20px'
    }
    if (miniMenu) {
      return '80px'
    }
    return '100px'
  }

  const filterMenuItems = useCallback(
    (menu: any) => {
      const {
        posthogFeatureFlagName,
        featureFlagName,
        hideIfFeatureFlagName,
        shouldDisableForBrowser,
        path,
        isInitialUser,
        roles,
        platform,
        ignorePlan,
        plan,
        onlyFeatureFlag,
        accessSetting,
        excludeLocationAdmin,
        allowLocationAdminApprover,
      } = menu

      if (isLoadingAuthUser) {
        return false
      }

      // Location admins are treated as Admin for accessSetting checks, so items flagged
      // excludeLocationAdmin must be hidden from them unless they are also an approver.
      if (excludeLocationAdmin && authUser.role === 'LocationAdmin' && authUser.approverTo.length === 0) {
        return false
      }

      // Feature flag checks
      if (posthogFeatureFlagName === FeatureFlagEnum.aiAssistedOnboarding && !aiAssistedOnboardingEnabled) {
        return false
      }

      if (posthogFeatureFlagName === FeatureFlagEnum.teamEnergy && !teamEnergyEnabled) {
        return false
      }

      if (featureFlagName && !featureFlags.includes(featureFlagName)) {
        return false
      }

      if (hideIfFeatureFlagName && featureFlags.includes(hideIfFeatureFlagName)) {
        return false
      }

      if (shouldDisableForBrowser) {
        return false
      }

      if (path === FrontendUrls.aiAssistant && !isInitialUser) {
        return false
      }

      // Dynamic access check via featurePermissions
      if (accessSetting) {
        const fv = resolvedPermissions.featureVisibility
        const allowedRoles = fv ? fv[accessSetting as keyof IFeatureVisibility] : undefined
        const roleForAccessCheck = authUser.role === 'LocationAdmin' ? 'Admin' : authUser.role
        if (Array.isArray(allowedRoles) && (allowedRoles as string[]).indexOf(roleForAccessCheck) === -1) {
          return false
        }
      } else {
        // Static role access checks (original behavior)
        const accessRole = roles && (roles.indexOf(authUser.role) !== -1 || (allowLocationAdminApprover && isApproverLocationAdmin))
        if (!accessRole) {
          return false
        }
      }

      // Platform access checks
      const accessPlatform = platform && platform.indexOf(authCompany?.platform as Platform) !== -1
      if (!accessPlatform) {
        return false
      }

      // Plan checks
      if (!ignorePlan && plan && plan !== effectivePlan && !onlyFeatureFlag) {
        return false
      }

      // Special case for toil requests
      if (path.endsWith('request-toil') && authUser.role === 'User') {
        const userCanRequestToil = authUser.location.leavePolicies?.find((lp) => lp.toil && lp.toilRequestsAllowedForUsers)
        if (!userCanRequestToil) {
          return false
        }
      }

      return true
    },
    [
      isLoadingAuthUser,
      aiAssistedOnboardingEnabled,
      teamEnergyEnabled,
      featureFlags,
      resolvedPermissions,
      isApproverLocationAdmin,
      authUser.role,
      authUser.approverTo,
      authUser.initialUser,
      authUser.location.leavePolicies,
      authCompany?.platform,
      effectivePlan,
    ]
  )

  const RenderMenuItem = (menu) => {
    const { title, path, icon, children, isLink, isSection, isDropdown, isCollapsible, roles, joyRideClass, tooltip, onClick, dynamicPath, allowLocationAdminApprover } = menu

    // Handle dynamic paths (e.g., recharge type based on user role)
    let finalPath = path
    if (dynamicPath) {
      if (dynamicPath === 'rechargeType') {
        // Admin users go to /app/users/:id, regular users go to /app/my-profile
        finalPath = authUser.role === 'Admin' ? `/app/users/${authUser.id}?activeTab=rechargeType` : '/app/my-profile?activeTab=rechargeType'
      }
    }

    // Handle section headers
    if (isSection) {
      if (menu.accessSetting) {
        const fv = resolvedPermissions.featureVisibility
        const allowedRoles = fv ? fv[menu.accessSetting as keyof IFeatureVisibility] : undefined
        const roleForAccessCheck = authUser.role === 'LocationAdmin' ? 'Admin' : authUser.role
        if (Array.isArray(allowedRoles) && (allowedRoles as string[]).indexOf(roleForAccessCheck) === -1) {
          return null
        }
      } else {
        const accessRole = roles && (roles.indexOf(authUser.role) !== -1 || (allowLocationAdminApprover && isApproverLocationAdmin))
        if (!accessRole) {
          return null
        }
      }

      // Handle collapsible sections
      if (isCollapsible && children) {
        const isCollapsed = collapsedSections.has(path)
        const filteredChildren = children.filter(filterMenuItems)

        if (filteredChildren.length === 0) {
          return null
        }

        return {
          key: path,
          type: 'group',
          icon: miniMenu && !isMobileDrawer ? <span className="main-menu-icon">{icon}</span> : null,
          label: (
            <div
              className="sidebar-section-header collapsible"
              onClick={() => toggleSectionCollapse(path)}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
              {miniMenu && !isMobileDrawer ? (
                <Tooltip placement="right" title={<IntlMessages id={title} />} className="ant-menu-inline-collapsed-tooltip">
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      gap: '2px',
                      width: '100%',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                      <span className="main-menu-icon">{icon}</span>
                      {isCollapsed ? <DownOutlined style={{ fontSize: '8px' }} /> : <UpOutlined style={{ fontSize: '8px' }} />}
                    </div>
                  </div>
                </Tooltip>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {/* Icon in the main non-collapsed menu section is hidden. If we need to show it, just uncomment the line below. */}
                    {/* <span className="main-menu-icon">{icon}</span> */}
                    <IntlMessages id={title} />
                  </div>
                  {isCollapsed ? <DownOutlined /> : <UpOutlined />}
                </>
              )}
            </div>
          ),
          children: isCollapsed
            ? []
            : filteredChildren
                .map((child) =>
                  RenderMenuItem({
                    ...child,
                    isInitialUser: authUser.initialUser,
                  })
                )
                .filter(Boolean),
        }
      }

      // Regular non-collapsible section
      return {
        key: path,
        type: 'group',
        icon: miniMenu && !isMobileDrawer ? <span className="main-menu-icon">{icon}</span> : null,
        label: (
          <div className="sidebar-section-header non-collapsible">
            {miniMenu && !isMobileDrawer ? (
              <Tooltip placement="right" title={<IntlMessages id={title} />} className="ant-menu-inline-collapsed-tooltip">
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                  }}
                >
                  <span className="main-menu-icon">{icon}</span>
                </div>
              </Tooltip>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <IntlMessages id={title} />
              </div>
            )}
          </div>
        ),
      }
    }

    // Handle dropdown menus (like Request Leave)
    if (isDropdown && children) {
      const accessRole = roles && roles.indexOf(authUser.role) !== -1
      if (!accessRole) {
        return null
      }
      return {
        key: path,
        label: (
          <>
            <span className="main-menu-icon">{icon}</span>
            <span>
              <IntlMessages id={title} />
            </span>
          </>
        ),
        className: joyRideClass || '',
        children: children
          .map((child) =>
            RenderMenuItem({
              ...child,
              isInitialUser: authUser.initialUser,
            })
          )
          .filter(Boolean),
      }
    }

    // Filtering logic now handled by filterMenuItems function

    // Handle onClick items (don't render as Link)
    if (onClick) {
      return {
        key: finalPath,
        icon: <span className="main-menu-icon">{icon}</span>,
        label: (
          <span>
            <IntlMessages id={title} />
          </span>
        ),
        className: joyRideClass || '',
        children: children ? renderSubMenuItem(menu) : null,
      }
    }

    return {
      key: finalPath,
      icon: <span className="main-menu-icon">{icon}</span>,
      label: (
        <>
          {!isLink ? (
            children ? (
              <span>
                <IntlMessages id={title} /> {tooltip && isBefore(new Date(), tooltip.hideAfter) && <Tag color="purple">{tooltip.tagTitle}</Tag>}
              </span>
            ) : (
              <span>
                <Link to={finalPath}>
                  <IntlMessages id={title} /> {tooltip && isBefore(new Date(), tooltip.hideAfter) && <Tag color="purple">{tooltip.tagTitle}</Tag>}
                </Link>
              </span>
            )
          ) : (
            <span>
              <a href={finalPath} target="_blank" rel="noopener noreferrer">
                <IntlMessages id={title} />
              </a>
            </span>
          )}
        </>
      ),
      className: joyRideClass || '',
      children: children ? renderSubMenuItem(menu) : null,
    }
  }

  const renderSubMenuItem = (menu) => {
    const { path, children, roles, platform } = menu

    const accessRole = roles && roles.indexOf(authUser.role) !== -1
    if (!accessRole) {
      return
    }

    const accessPlatform = platform && platform.indexOf(authCompany?.platform) !== -1
    if (!accessPlatform) {
      return
    }

    return children.map((child) => {
      const copy = { ...child }
      copy.path = copy.isLink || copy.hasCleanPath ? child.path : `${path}${child.path}`

      return RenderMenuItem(copy)
    })
  }

  // Create Request Leave dropdown menu items
  const getRequestLeaveDropdownItems = () => {
    const items = requestLeaveOptions
      .filter((option) => {
        const accessRole = option.roles && option.roles.indexOf(authUser.role) !== -1
        if (!accessRole) return false

        const accessPlatform = option.platform && option.platform.indexOf(authCompany?.platform as Platform) !== -1
        if (!accessPlatform) return false

        if (option.path.endsWith('request-toil')) {
          return canShowRequestToil({
            role: authUser.role,
            plan: effectivePlan,
            hasToilFeatureFlag: featureFlags.includes(FeatureFlagEnum.toil),
            companyToilEnabled: authCompany?.toilEnabled,
            ownLocationLeavePolicies: authUser.location.leavePolicies,
          })
        }
        return true
      })
      .map((option, index) => ({
        key: option.path,
        label: (
          <Link
            to={option.path}
            onClick={() => {
              handleItemClickForMobile()
              sidebarTracker.trackRequestLeaveCTAOptionClicked({
                optionName: option.title,
                optionRoute: option.path,
                optionIndex: index,
                totalOptions: requestLeaveDropdownItems.length,
                isMiniMenu: miniMenu,
                isMobileDrawer,
              })
            }}
          >
            <span style={{ marginLeft: 8 }}>
              <IntlMessages id={option.title} />
            </span>
          </Link>
        ),
      }))

    return items
  }

  const requestLeaveDropdownItems = getRequestLeaveDropdownItems()

  return (
    <div className="sidebar-container">
      {!isMobileDrawer && <SidebarLogo />}

      <div className="layout-sidebar-content" style={{ paddingTop: isMobileDrawer ? '20px' : '0' }}>
        {/* Request Leave Button */}
        {requestLeaveDropdownItems.length > 0 && (
          <div style={{ padding: miniMenu && !isMobileDrawer ? '0 8px 8px 8px' : '0 24px 16px 24px' }}>
            {requestLeaveDropdownItems.length === 1 ? (
              // Single option - render as direct CTA button
              <Link to={(requestLeaveDropdownItems[0] as { key: string }).key} style={{ display: 'block' }}>
                <Button
                  type="primary"
                  block
                  size="large"
                  className="joyride-request-leave request-leave-single-cta"
                  style={{
                    background: '#722ed1',
                    borderColor: '#722ed1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    const option = requestLeaveOptions.find((opt) => opt.path === (requestLeaveDropdownItems[0] as { key: string }).key)
                    if (option) {
                      handleItemClickForMobile()
                      sidebarTracker.trackRequestLeaveCTAOptionClicked({
                        optionName: option.title,
                        optionRoute: option.path,
                        optionIndex: 0,
                        totalOptions: 1,
                        isMiniMenu: miniMenu,
                        isMobileDrawer,
                      })
                    }
                  }}
                >
                  {(!miniMenu || isMobileDrawer) && <IntlMessages id="app.requestLeave" />}
                  <PiLightning />
                </Button>
              </Link>
            ) : (
              // Multiple options - render as dropdown
              <Dropdown
                menu={{ items: requestLeaveDropdownItems }}
                trigger={['hover']}
                placement="bottomLeft"
                onOpenChange={(open) => {
                  if (open) {
                    sidebarTracker.trackRequestLeaveCTAHover({
                      availableOptionsCount: requestLeaveDropdownItems.length,
                      availableOptions: requestLeaveDropdownItems.map((item) => (item as { key: string }).key),
                      isMiniMenu: miniMenu,
                      isMobileDrawer,
                    })
                  }
                }}
              >
                <Button
                  type="primary"
                  block
                  size="large"
                  className="joyride-request-leave"
                  style={{
                    background: '#722ed1',
                    borderColor: '#722ed1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  {(!miniMenu || isMobileDrawer) && <IntlMessages id="app.requestLeave" />}
                  <PiLightning />
                </Button>
              </Dropdown>
            )}
          </div>
        )}
        {hasWizardState && (
          <div className="vt-wizard-cta" style={{ padding: miniMenu && !isMobileDrawer ? '4px 8px 8px 8px' : '4px 24px 12px 24px' }}>
            <Link to={FrontendUrls.setup} onClick={isMobileDrawer ? handleItemClickForMobile : undefined}>
              <StarOutlined />
              {(!miniMenu || isMobileDrawer) && (
                <span>
                  <IntlMessages id="onboarding.setup.sidebar.continueSetup" />
                </span>
              )}
            </Link>
          </div>
        )}

        <CustomScrollbars
          className="layout-sider-scrollbar"
          style={{
            height: isMobileDrawer ? '100%' : miniMenu ? 'calc(100% - 64px)' : 'calc(100% - 80px)',
          }}
        >
          <div style={{ paddingBottom: getScrollableContentPadding() }}>
            <Menu
              className="main-menu"
              theme="light"
              mode="inline"
              selectedKeys={selectedKeys}
              defaultOpenKeys={!miniMenu || isMobileDrawer ? openKeys : undefined}
              openKeys={!miniMenu || isMobileDrawer ? openKeys : undefined}
              onClick={handleMenuClick}
              onOpenChange={handleOpenChange}
              items={
                menus
                  .filter(filterMenuItems)
                  .map((menu) => {
                    return RenderMenuItem({
                      ...menu,
                      isInitialUser: authUser.initialUser,
                    })
                  })
                  .filter(Boolean) as MenuProps['items']
              }
            />

            <div className="sidebar-bottom">
              {!(hideOnboardingFlow || onboardingCompleted || onboardingSkipped || onboardingV2Enabled) && (
                <Tooltip placement="right" title={<IntlMessages id="sidebar.onboarding" />} classNames={{ root: !miniMenu ? 'display-none' : '' }}>
                  <Button type="link" className="onboarding" onClick={() => dispatch(handleOnboardingVisible())}>
                    <Badge count={`${progressCompleted}/${totalChecklist}`} offset={[15, 0]} style={{ backgroundColor: '#52c41a' }}>
                      <StarOutlined />{' '}
                      <span className="text">
                        <IntlMessages id="sidebar.onboarding" />
                      </span>
                    </Badge>
                  </Button>
                </Tooltip>
              )}
              {authUser.role === 'Admin' &&
                authCompany?.trialPeriod === 'in_trial' &&
                authCompany.billing?.paymentProcessor !== 'microsoft-billing' &&
                !authCompany?.billing?.couponCodes?.includes(SURVEY_TRIAL_EXTEND) &&
                authCompany?.timestamp &&
                differenceInDays(new Date(), new Date(authCompany.timestamp)) >= 2 && (
                  <Tooltip placement="right" title={<IntlMessages id="app.trialExtend" />} classNames={{ root: !miniMenu ? 'display-none' : '' }}>
                    <Link className="changelog" to={`${FrontendUrls.home}?showSurvey=true`}>
                      <GiftOutlined />
                      <IntlMessages id="app.trialExtend" />
                    </Link>
                  </Tooltip>
                )}
            </div>
          </div>
        </CustomScrollbars>

        {/* User Profile - Sticky at bottom */}
        {
          <div className="sidebar-profile">
            <Dropdown
              menu={{ items: profileMenuItems, className: 'sidebar-profile-menu' }}
              trigger={['hover']}
              placement="topRight"
              onOpenChange={(open) => {
                if (open) {
                  sidebarTracker.trackProfileMenuOpened({
                    isMiniMenu: miniMenu,
                    triggerType: 'hover',
                  })
                }
              }}
            >
              <Button type="link" className="sidebar-profile-button">
                <div className="sidebar-profile-wrapper">
                  {(!miniMenu || isMobileDrawer) && (
                    <div className="sidebar-profile-info">
                      <Typography.Text className="sidebar-profile-name">{authUser.name}</Typography.Text>
                      <Typography.Text className="sidebar-profile-company" type="secondary">
                        {authCompany?.name}
                      </Typography.Text>
                    </div>
                  )}
                  <div style={{ position: 'relative' }}>
                    <UserAvatar
                      id={authUser.id}
                      name={authUser.name}
                      className="sidebar-profile-avatar"
                      shape="circle"
                      avatar={authUser.imageUrl}
                      showEnergy={shouldShowEnergyScore}
                      energyScore={energyScore}
                    />
                  </div>
                </div>
              </Button>
            </Dropdown>
          </div>
        }
      </div>
    </div>
  )
}

// Memoize to prevent unnecessary re-renders when parent re-renders
// Only re-render when props actually change
export default React.memo(SidebarContent, (prevProps, nextProps) => {
  // Return true if props are equal (should NOT re-render)
  // Return false if props are different (should re-render)
  return prevProps.onLogout === nextProps.onLogout && prevProps.isMobileDrawer === nextProps.isMobileDrawer && prevProps.onItemClick === nextProps.onItemClick
})
